// hooks/useAIChat.js – v4.2 (OpenAI-native, always sends valid threadId, robust chip/buttons logic)

import { useState } from 'react';
import axios from '../api/axios';

// Helper: always send a threadId (guaranteed unique)
function genThreadId() {
  return 'thread-' + Date.now() + '-' + Math.floor(Math.random() * 100000);
}

// Helper: pick best message to show for any AI response object
function getBestMsg(ai) {
  return (
    (typeof ai.msg === "string" && ai.msg.trim() !== "")
      ? ai.msg
      : ai.aiReply || ai.answer || ai.clarifier || ai.summary || "🤖 Sorry, I did not understand that."
  );
}

export default function useAIChat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [threadId, setThreadId] = useState(null);
  const [suggestionChips, setSuggestionChips] = useState([
    'Create Client', 'Show Invoices', 'Update Task', 'Record Payment'
  ]);

  const now = () => new Date();

  // Append new message to chat history
  const pushMessage = (msg) =>
    setMessages((prev) => [...prev, { ...msg, time: now() }]);

  // Create smart AI CRUD/view/edit/delete buttons from AI backend result
  const makeSmartButtons = (result = {}) => {
    if (!result) return null;
    const entityKeys = {
      invoiceId: 'invoice',
      taskId: 'task',
      paymentId: 'payment',
      clientId: 'client'
    };
    for (const [idKey, ent] of Object.entries(entityKeys)) {
      if (result[idKey]) {
        const id = result[idKey];
        return [
          { label: `🔍 View ${ent}`, prompt: `view ${ent} ${id}` },
          { label: `✏️ Update ${ent}`, prompt: `update ${ent} ${id}` },
          { label: `🗑 Delete ${ent}`, prompt: `delete ${ent} ${id}` }
        ];
      }
    }
    return null;
  };

  // Core: send user input to backend, update threadId and messages
  async function handleSend(forcedInput = null, params = {}, overrideThreadId = null) {
    const userText = forcedInput ?? input.trim();
    if (!userText && !params?.selectorConfirm && !params?.selectorReject) return;

    if (userText) {
      pushMessage({ role: 'user', msg: userText });
      setInput('');
    }
    setLoading(true);

    // Reset thread for main commands (prevents stale flow carryover)
    if (/^(create|add|delete|remove|update|edit|view|show|record|log|schedule|reset)\b/i.test(userText)) {
      setThreadId(null);
    }

    try {
      // Always generate/propagate a threadId
      const activeThreadId = overrideThreadId || threadId || genThreadId();
      const { data: ai } = await axios.post('/ask-ai', {
        userMessage: userText,
        threadId: activeThreadId
        // Optionally: userContext: {...}
      });

      // Update threadId (use backend's preferred, else keep our own)
      if (ai.threadId) setThreadId(ai.threadId);
      else setThreadId(activeThreadId);

      // PATCH: Split side-intent/faq + main reminder as separate bubbles
      if (
        (ai.aiReply || ai.msg || ai.answer) &&
        ai.reminder &&
        ai.status === 'needs_input'
      ) {
        // FAQ/side intent
        pushMessage({
          role: 'assistant',
          status: 'side_intent',
          msg: getBestMsg(ai),
          ...ai,
          reminder: undefined
        });
        // Main reminder
        if (typeof ai.reminder === 'object' && ai.reminder !== null && ai.reminder.msg) {
          pushMessage({
            role: 'assistant',
            status: 'reminder',
            msg: ai.reminder.msg,
            time: now(),
          });
        } else if (typeof ai.reminder === 'string') {
          pushMessage({
            role: 'assistant',
            status: 'reminder',
            msg: ai.reminder,
            time: now(),
          });
        }
        setLoading(false);
        return;
      }

      // Main status-driven render logic
      switch (ai.status) {
        case 'needs_disambiguation': {
          pushMessage({
            role: 'assistant',
            status: ai.status,
            msg: getBestMsg(ai),
            ...ai
          });
          break;
        }
        case 'validation_error': {
          pushMessage({
            role: 'assistant',
            status: ai.status,
            msg: getBestMsg(ai),
            ...ai
          });
          break;
        }
        case 'needs_input': {
          pushMessage({
            role: 'assistant',
            status: ai.status,
            msg: getBestMsg(ai),
            ...ai
          });
          break;
        }
        case 'needs_confirmation': {
          const step = ai.plan?.[0] || {};
          pushMessage({
            role: 'assistant',
            status: ai.status,
            summary: step.confirmationSummary || step.summary || ai.summary,
            msg: ai.clarifier || ai.msg || ai.aiReply || ai.answer || ai.summary,
            action: step.action,
            entity: step.entity,
            ...ai
          });
          break;
        }
        case 'ok': {
          pushMessage({
            role: 'assistant',
            status: ai.status,
            msg: getBestMsg(ai),
            ...ai
          });
          break;
        }
        case 'error':
        case 'execution_error': {
          pushMessage({
            role: 'assistant',
            status: ai.status,
            msg: ai.error || getBestMsg(ai) || '❌ Error.',
            ...ai
          });
          setThreadId(null);
          break;
        }
        case 'ready':
        case 'done':
        case 'success': {
          if ((ai.results || []).length > 0) {
            ai.results.forEach((r, i) => {
              pushMessage({
                role: 'assistant',
                status: ai.status,
                ...ai,
                ...r,
                summary: r.summary,
                msg: r.msg || r.summary || r.aiReply,
                buttons: makeSmartButtons(r)
              });
            });
          } else {
            pushMessage({
              role: 'assistant',
              status: ai.status,
              summary: ai.summary,
              msg: ai.summary || getBestMsg(ai) || '✅ Success.',
              ...ai
            });
          }
          setThreadId(null);
          break;
        }
        default: {
          pushMessage({
            role: 'assistant',
            status: ai.status,
            msg: getBestMsg(ai),
            ...ai
          });
        }
      }

      // Smart suggestions from backend (OpenAI or tool registry)
      if (Array.isArray(ai.suggestions) && ai.suggestions.length) {
        setSuggestionChips(ai.suggestions);
      }
    } catch (err) {
      console.error('[useAIChat] Network error:', err);
      pushMessage({ role: 'assistant', status: 'error', msg: '❌ Network error. Please try again.' });
      setThreadId(null);
    } finally {
      setLoading(false);
    }
  }

  // -- Handlers for AI chat UI (unchanged) --
  const sendPrompt = ({ prompt, params = {}, threadId: overrideThreadId }) =>
    handleSend(prompt, params, overrideThreadId);

  const handleQuickPrompt = (p) => handleSend(p);
  const handleButtonClick = (p) => handleSend(p);

  const handleConfirm = (answer) => {
    handleSend(answer);
    setInput('');
  };

  const handleDisambiguationConfirm = (candidate, msgObj) => {
    if (!candidate || !msgObj) return;
    handleSend('', {
      selectorConfirm: true,
      entity: msgObj.entity,
      selectedId: candidate._id,
      fields: msgObj.fields
    }, msgObj.threadId);
    setInput('');
  };
  const handleDisambiguationReject = () => {
    setInput('');
  };

  return {
    messages,
    input,
    setInput,
    loading,
    suggestionChips,
    handleSend,
    handleQuickPrompt,
    handleButtonClick,
    handleConfirm,
    sendPrompt,
    handleDisambiguationConfirm,
    handleDisambiguationReject
  };
}
